<template>
  <fieldset
    ref="$fieldset"
    class="RadioButtonGroupComponent"
    :class="{ 'v-input--error': fieldValidationErrorMessage }"
  >
    <legend>{{ legend }}</legend>

    <div
      class="options"
      :class="[
        [`direction--${flexDirection}`, { scroll: scroll, 'v-input--disabled': !editable }],
        flexWrap
      ]"
    >
      <div
        v-for="(option, i) in options"
        :key="option.value"
        class="choice"
      >
        <p
          v-if="flexDirection === 'column' && optionLabels[i]"
          class="option-label"
        >
          {{ optionLabels[i] }}
        </p>

        <RadioButtonComponent
          :id="id"
          :key="option.value"
          :label="option.label"
          :value="option.value"
          :disabled="!editable || option.disabled"
          :selected="option.value === innerValue"
          :isTabbable="
            option.value === innerValue || (!hasValidSelection && i === firstEnabledIndex)
          "
          :small="small"
          @change="onChange"
          @focus="onFocus"
          @keydown="onKeydown"
        />
        <slot :name="option.value"></slot>
        <CardInfoComponent
          v-if="option.extra?.alert && option.value === innerValue"
          :icon="option.extra.alert.icon"
        >
          {{ option.extra.alert.label }}
        </CardInfoComponent>
      </div>
    </div>

    <div
      ref="hiddenFieldWrapper"
      style="display: none"
      aria-hidden="true"
    >
      <v-text-field
        :key="String(innerValue)"
        ref="$field"
        :id="`${id}-hidden`"
        aria-hidden="true"
        :aria-describedby="undefined"
        tabindex="-1"
        v-model="innerValue"
        :rules="validations"
        :hide-details="true"
      />
    </div>

    <ErrorMessageComponent
      :messages="fieldValidationErrorMessage"
      :id="id + '-error'"
    />
  </fieldset>
</template>

<script lang="ts" setup>
  import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'
  import RadioButtonComponent from './RadioButtonComponent.vue'

  import CardInfoComponent from '@/modules/common/common/components/panels/CardInfoComponent.vue'
  import type { OptionsValuesInterface, ValidationRuleType } from '@/modules/core/models/types'
  import {
    endDuplicatedLabelControl,
    startDuplicatedLabelControl
  } from '../../composables/accesibilityComposable'

  const { t } = useI18n()

  interface PropsInterface {
    id: string
    legend?: string
    options: OptionsValuesInterface[]
    value?: string | number | null
    modelValue?: string | number | null
    flexDirection?: string
    flexWrap?: string
    scroll?: boolean
    rules?: ValidationRuleType[]
    small?: boolean
    editable?: boolean
    optionLabels?: string[] // * available for "flexDirection:column"
  }

  const props = withDefaults(defineProps<PropsInterface>(), {
    options: () => [],
    flexDirection: 'column',
    flexWrap: '',
    scroll: false,
    editable: true,
    rules: () => [],
    optionLabels: () => []
  })
  const emit = defineEmits(['update:modelValue'])

  const innerValue = ref(props.value)
  const currentFocusIndex = ref(0)
  const firstEnabledIndex = computed(() => props.options.findIndex((option) => !option.disabled))
  const hasValidSelection = computed(
    () => !!innerValue.value && props.options.some((o) => o.value === innerValue.value)
  )

  //TODO: Move error messages to locales
  const validationRules = {
    valid: (value: string) =>
      (!!value && props.options.some((elm) => elm.value === value)) ||
      t('common.validations.option')
  }

  const validations: ValidationRuleType[] = [...props.rules, validationRules.valid]

  watch(
    () => props.value,
    (value) => {
      if (value !== props.modelValue) {
        setValue(value ?? null)
      }
    },
    { immediate: true } // * to set default values in Step3HealthConditionsForm
  )

  function onChange(e: Event) {
    const { value = '' } = e.target as HTMLInputElement
    setValue(value)
  }

  function onFocus(e: Event) {
    const input = e.target as HTMLInputElement
    const { value } = input
    const index = props.options.findIndex((option) => option.value === input.value)
    currentFocusIndex.value = index
    setValue(value)
  }

  const $fieldset = ref<HTMLElement | null>(null)

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const target = e.target as HTMLElement
      const input =
        target.tagName === 'INPUT'
          ? (target as HTMLInputElement)
          : (target.querySelector('input[type="radio"]') as HTMLInputElement)
      if (input?.value) {
        input.checked = true
        setValue(input.value)
      }
      // Move focus to slot content if revealed, otherwise past the radio group
      nextTick(() => {
        if (!$fieldset.value) return
        const focusableSelector =
          'a[href], button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
        const allFocusable = Array.from(document.querySelectorAll<HTMLElement>(focusableSelector))
        const fieldsetEl = $fieldset.value

        // Check if the selected option revealed slot content with focusable elements
        if (input) {
          const radioIdx = allFocusable.indexOf(input)
          if (radioIdx >= 0) {
            for (let i = radioIdx + 1; i < allFocusable.length; i++) {
              const el = allFocusable[i]
              if (!fieldsetEl.contains(el)) break
              if (el.getAttribute('type') !== 'radio') {
                el.focus()
                return
              }
            }
          }
        }

        // Default: move focus past the radio group
        const lastInGroup = allFocusable.filter((el) => fieldsetEl.contains(el)).pop()
        if (lastInGroup) {
          const idx = allFocusable.indexOf(lastInGroup)
          const nextEl = allFocusable[idx + 1]
          nextEl?.focus()
        }
      })
      return
    }

    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      return
    }

    e.preventDefault()

    const enabledOptions = props.options.filter((option) => !option.disabled)
    const currentIndex = enabledOptions.findIndex((option) => option.value === innerValue.value)
    let nextIndex = currentIndex

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % enabledOptions.length
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      nextIndex = currentIndex <= 0 ? enabledOptions.length - 1 : currentIndex - 1
    }

    const nextOption = enabledOptions[nextIndex]
    if (nextOption) {
      setValue(nextOption.value)

      nextTick(() => {
        const radioInput = document.getElementById(
          `${props.id}-${nextOption.value}`
        ) as HTMLInputElement
        if (radioInput) {
          radioInput.focus()
        }
      })
    }
  }

  function setValue(value: string | number | null) {
    innerValue.value = value
    emit('update:modelValue', value)
  }

  const { $field, fieldValidationErrorMessage } = useValidationError()

  const hiddenFieldWrapper = ref<HTMLElement | null>(null)
  let hiddenObserver: MutationObserver | null = null

  onMounted(() => {
    hiddenObserver = startDuplicatedLabelControl(hiddenFieldWrapper.value, {
      labelOrphanedInputs: true
    })
  })
  onUnmounted(() => endDuplicatedLabelControl(hiddenObserver))
</script>

<style lang="scss" scoped>
  .RadioButtonGroupComponent {
    border: none;
    margin-top: $spacing-l;

    legend {
      font-family: $font-family-primary;
      font-size: $font-size-m;
      font-style: normal;
      font-weight: $font-weight-400;
      line-height: $line-height-100p;
      color: $color-neutral-dark;
      margin-bottom: $spacing-s;

      &:empty {
        margin: 0;
      }
    }

    .options {
      display: flex;
      gap: $gap-m;

      &.v-input--disabled {
        filter: opacity(1);

        .choice {
          :deep(.card) {
            background-color: $color-grey-400;
            color: $color-neutral;
            border-color: $color-grey-500;

            input:checked + .input {
              box-shadow: inset 0 0 0 4px $color-neutral;
            }
          }
        }
      }

      &.wrap {
        flex-wrap: wrap;

        .choice {
          flex: 1 1 48%;
          max-width: 48%;
        }
      }

      &.direction {
        &--row {
          flex-direction: row;

          .choice {
            flex: 1;

            .RadioButtonComponent {
              :deep(.card) {
                &.small {
                  padding: $spacing-l $spacing-xl;
                }

                height: $size-full;
              }
            }
          }

          &.scroll {
            overflow-x: auto;
            padding: $spacing-s;

            .choice {
              flex: 1 0 auto;
              max-width: $size-180;
            }

            :deep(.card) {
              padding: $spacing-s $spacing-l;
              border-color: $color-neutral-lighter;

              &.selected {
                border-color: $color-primary;
              }
            }

            @include breakpoint-md {
              max-width: var(--custom-max-width-md, $size-470);
            }

            @include breakpoint-xl {
              max-width: unset;
            }
          }
        }

        &--column {
          flex-direction: column;

          .choice .option-label {
            color: $color-neutral;
            margin-bottom: $spacing-s;
          }
        }
      }
    }

    &.v-input--error {
      :deep(.card) {
        border-color: $color-error;
        background-color: $color-error-200;
      }
    }
  }
</style>
